import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@/components/ui/Button";
import { getV2Error } from "@/services/api/v2";
import { MatchEvent, matchKeys, matchesV2API } from "@/services/api/matchesV2";
import SectionQuery from "../detail/SectionQuery";
import { useMatchDirtyGuard } from "@/context/MatchRuntimeContext";

const EventsEditor: React.FC<{
  workspaceId: string;
  id: string;
  canEdit: boolean;
}> = ({ workspaceId, id, canEdit }) => {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: matchKeys.section(workspaceId, "manual", id, "events"),
    queryFn: () => matchesV2API.events(workspaceId, id),
  });
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [dirty, setDirty] = useState(false);
  useMatchDirtyGuard(`match-events-${id}`, dirty, "event changes");
  const save = useMutation({
    mutationFn: () => matchesV2API.saveEvents(workspaceId, id, events),
    onSuccess: (value) => {
      client.setQueryData(
        matchKeys.section(workspaceId, "manual", id, "events"),
        value,
      );
      setEvents(value.data);
      setDirty(false);
    },
    onError: () => undefined,
  });
  useEffect(() => {
    if (query.data && !dirty) setEvents(query.data.data);
  }, [dirty, query.data]);
  const change = (index: number, patch: Partial<MatchEvent>) => {
    const next = [...events];
    next[index] = { ...next[index], ...patch };
    setEvents(next);
    setDirty(true);
  };
  return (
    <SectionQuery title="Events" query={query}>
      <div className="space-y-3">
        <ul className="space-y-3">
          {events.map((event, index) => (
            <li
              key={event.id || index}
              className="grid gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800 sm:grid-cols-[6rem_9rem_8rem_1fr_auto]"
            >
              <label className="text-sm">
                Minute
                <input
                  disabled={!canEdit}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                  type="number"
                  min="0"
                  max="130"
                  value={event.minute ?? ""}
                  onChange={(value) =>
                    change(index, {
                      minute: value.target.value
                        ? Number(value.target.value)
                        : null,
                    })
                  }
                />
              </label>
              <label className="text-sm">
                Type
                <select
                  disabled={!canEdit}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                  value={event.type}
                  onChange={(value) =>
                    change(index, {
                      type: value.target.value as MatchEvent["type"],
                    })
                  }
                >
                  {[
                    "goal",
                    "shot",
                    "card",
                    "sub",
                    "foul",
                    "chance",
                    "custom",
                  ].map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                Team
                <select
                  disabled={!canEdit}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                  value={event.team}
                  onChange={(value) =>
                    change(index, {
                      team: value.target.value as MatchEvent["team"],
                    })
                  }
                >
                  <option value="own">Own</option>
                  <option value="opp">Opponent</option>
                </select>
              </label>
              <label className="text-sm">
                Player ID (optional)
                <input
                  disabled={!canEdit}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                  value={event.playerId || ""}
                  onChange={(value) =>
                    change(index, { playerId: value.target.value || null })
                  }
                />
              </label>
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setEvents((value) =>
                      value.filter((_, position) => position !== index),
                    );
                    setDirty(true);
                  }}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEvents((value) => [
                  ...value,
                  {
                    minute: null,
                    type: "custom",
                    team: "own",
                    playerId: null,
                    details: {},
                  },
                ]);
                setDirty(true);
              }}
            >
              Add event
            </Button>
            <Button
              loading={save.isPending}
              loadingLabel="Saving match events"
              disabled={!dirty}
              onClick={() => save.mutate()}
            >
              Save events
            </Button>
          </div>
        )}
        {save.isSuccess && <p role="status">Events saved.</p>}
        {save.isError && (
          <p role="alert" className="text-red-700 dark:text-red-300">
            {getV2Error(save.error).message}
          </p>
        )}
      </div>
    </SectionQuery>
  );
};
export default EventsEditor;
